import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { imageService } from '@bedrock/fido-sdk';
import { IMAGE_FIT_SCALE_CROP, DEFAULT_IMAGE_FORMAT, IMAGE_QUALITY } from '@bedrock-rdk/constants';
import { Row } from '../row/row.component';
import { getTitle } from '../templates/blocks/utils/block';
import { getLayoutTheme } from '../utils/theme';
import {
  IMPRESSION_TRACK_SIGNAL,
  IMPRESSION_UNTRACK_SIGNAL,
  PORTION_REQUESTED_SIGNAL,
  SELECTED_SIGNAL,
  SHOW_GRID_SIGNAL,
} from '../signals';
import {
  colors,
  getBlockStyle,
  getInitialStyle,
  defaultContext,
  EMPTY_BLOCK_STYLE,
  BACKGROUND_INITIAL_ALPHA,
  BACKGROUND_LOADED_ALPHA,
} from './block.style';

export class Block extends Lightning.Component {
  static _template() {
    return _merge(
      {
        rect: true,
        Background: {},
        Title: {},
        Row: {
          type: Row,
        },
        passSignals: {
          [SELECTED_SIGNAL]: true,
          [SHOW_GRID_SIGNAL]: true,
          [PORTION_REQUESTED_SIGNAL]: true,
          [IMPRESSION_TRACK_SIGNAL]: true,
          [IMPRESSION_UNTRACK_SIGNAL]: true,
        },
      },
      getInitialStyle(),
    );
  }

  static getOffsetsByBlockData({ data: { title }, screenSize, blockIndex, isSearchPage }) {
    const hasTitle = Boolean(getTitle(title));

    const {
      items,
      padding: { bottom },
    } = getBlockStyle({
      hasTitle,
      isFirstBlock: blockIndex === 0,
      screenSize,
      isSearchPage,
    });

    return {
      top: Number.isFinite(blockIndex) ? items.y : 0,
      bottom,
    };
  }

  _construct() {
    this._contentPadding = 0;
    this._style = { ...defaultContext };
  }

  _build() {
    this.patch(getInitialStyle(this._style));
  }

  _attach() {
    const background = this.tag('Background');
    background.alpha = BACKGROUND_INITIAL_ALPHA;
    background.on('txLoaded', () => {
      background.setSmooth('alpha', BACKGROUND_LOADED_ALPHA);
    });
  }

  _detach() {
    this._data = undefined;
    this.tag('Background').removeAllListeners('txLoaded');
  }

  get data() {
    return this._data;
  }

  set data(value) {
    this._data = value;

    const { block, blockIndex, theme, rowSize } = this._data;
    const { colorScheme, backgroundColor, backgroundImage } = getLayoutTheme(block.theme, theme);
    const backgroundImageURL = backgroundImage
      ? imageService.getUrl(backgroundImage, rowSize.w, rowSize.h, {
          fit: IMAGE_FIT_SCALE_CROP,
          quality: IMAGE_QUALITY,
          format: DEFAULT_IMAGE_FORMAT,
        })
      : null;

    const titleText = getTitle(block.title);
    const hasTitle = Boolean(titleText);
    const blockStyle = Number.isFinite(blockIndex)
      ? getBlockStyle({
          hasTitle,
          isFirstBlock: blockIndex === 0,
          screenSize: this._style.screenSize,
          isSearchPage: this._data.isSearchPage,
        })
      : EMPTY_BLOCK_STYLE;
    this._style.colorMode = colorScheme;

    this.patch({
      ...rowSize,
      // NOTE: a `rect` with no colour, and a Background with no image, still submit a full width quad each:
      // ElementCore only skips an element whose render alpha is zero, it does not look at the colour alpha
      rect: Boolean(backgroundColor),
      color: backgroundColor,
      Background: {
        visible: Boolean(backgroundImageURL),
        src: backgroundImageURL,
      },
      Title: {
        visible: hasTitle,
        color: colors.title[colorScheme],
        text: { text: titleText },
        ...blockStyle.title,
      },
      Row: {
        ...blockStyle.items,
      },
    });

    this.tag('Row').data = value;
  }

  get contentPadding() {
    return this._contentPadding;
  }

  set contentPadding(value) {
    if (Number.isFinite(value) && this._contentPadding !== value) {
      this.tag('Title').x = value;
      this.tag('Row').patch({
        x: value,
        leftPadding: value,
      });
      this._contentPadding = value;
    }
  }

  get contentGap() {
    return this.tag('Row').contentGap;
  }

  set contentGap(value) {
    this.tag('Row').contentGap = value;
  }

  get startPointMode() {
    return this.tag('Row').startPointMode;
  }

  set startPointMode(value) {
    this.tag('Row').startPointMode = value;
  }

  set focusMode(value) {
    this.tag('Row').focusMode = value;
  }

  get focusedIndex() {
    return this.tag('Row').focusedIndex;
  }

  set focusedIndex(value) {
    this.tag('Row').focusedIndex = value;
  }

  goTo(index) {
    this.tag('Row').goTo(index);
  }

  _getFocused() {
    return this.tag('Row');
  }
}
